/**
 * Decide whether a server's error message is fit for a person to read, and
 * supply copy when it is not.
 *
 * ## Why this exists
 *
 * The signup eligibility gate rendered **"ThrottlerException: Too Many
 * Requests"** verbatim in an error Alert. That is a NestJS exception class name
 * concatenated with an HTTP status reason — framework noise that reached a
 * public, unauthenticated screen because the page's `extractMessage` relayed
 * `response.data.message` whenever one was present, and a `ThrottlerException`
 * response always has one.
 *
 * Two separate faults in one string:
 *
 * 1. It tells the reader nothing they can act on. Somebody who has typed one
 *    email address and pressed Continue cannot tell whether they broke
 *    something, whether Vera is down, or whether to try again — and the one
 *    thing that IS true (wait, then retry) is the thing it does not say.
 * 2. It leaks the server's shape. A class name is an implementation detail; on
 *    the signup page it is visible to anybody on the internet.
 *
 * ## The rule
 *
 * A server message is relayed ONLY when it reads as something a human wrote for
 * a human. Everything else falls back to copy the call site supplies, which is
 * already contextual ("Could not start checkout") and already vetted.
 *
 * ## What must still pass through
 *
 * This is the half that makes the filter dangerous if it is drawn too wide.
 * Several signup refusals are deliberate, human-readable server copy and are
 * the ONLY explanation the reader gets:
 *
 *   - "This email is not eligible for self-signup."   (join / create-company)
 *   - "An account already exists for this email."
 *   - "No pending subscription found for this payment."
 *
 * None of them matches a predicate below, and that is asserted by
 * `serverErrorMessage.guard.spec.ts` rather than left to inspection — an
 * over-broad regex here would silently replace a real refusal with a generic
 * "something went wrong", which is the failure mode `apps/app`'s `otpError.ts`
 * documents from the other direction: a wrong error message is worse than a
 * vague one.
 *
 * ## Why this is a platform copy rather than an import
 *
 * `apps/app/src/utils/otpError.ts` reached the same conclusions first and its
 * `isRawHttpStatus` / `isInternalDetail` predicates are mirrored here. It is
 * NOT imported: `apps/app` and `apps/platform` share no module graph, and
 * cross-app imports are not a pattern this repo has. The shared home, if a
 * third surface needs it, is `@repo/ui`-adjacent shared code — not one app
 * reaching into another.
 */

/** 429. Says the one true thing: waiting is what fixes it. */
export const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Wait a few minutes and try again.'

/**
 * A bare HTTP status reason ("Bad Request", "Forbidden") is framework noise,
 * not copy. Matched as the WHOLE string — a deliberate sentence that happens to
 * contain the words "not found" is not this.
 */
const isRawHttpStatus = (message: string): boolean =>
  /^(bad request|unauthorized|payment required|forbidden|not found|method not allowed|not acceptable|request timeout|conflict|gone|payload too large|unprocessable entity|too many requests|internal server error|not implemented|bad gateway|service unavailable|gateway timeout)\.?$/i.test(
    message.trim(),
  )

/**
 * A framework exception class name. `ThrottlerException: Too Many Requests` is
 * the live instance; `TypeError: x is not a function` and
 * `QueryFailedError: ...` are the same shape from other layers.
 *
 * Deliberately anchored: the token must either START the message or be
 * immediately followed by a colon. A looser `/Error|Exception/` would match
 * ordinary prose ("There was an error"), which is exactly the over-broad filter
 * that swallows real copy.
 */
const isFrameworkException = (message: string): boolean => {
  const trimmed = message.trim()
  return (
    /^[A-Z][A-Za-z0-9_]*(Exception|Error)\b/.test(trimmed) ||
    /[A-Z][A-Za-z0-9_]*(Exception|Error):/.test(trimmed)
  )
}

/**
 * Server-side detail that must never reach a screen — a rejected query, a
 * stack frame, an internal identifier, a transport-level failure code.
 * Mirrors `otpError.ts`'s own list, which was written from a real incident
 * where an INSERT statement was about to be rendered to a customer.
 */
const isInternalDetail = (message: string): boolean =>
  /failed query|insert into|select .* from|update .* set|drizzle|postgres|\bat [A-Z]\w+\.|node_modules|econnrefused|etimedout|enotfound|socket hang up/i.test(
    message,
  )

/** True when the message reads as copy a person wrote for a person. */
export const isReadableServerMessage = (message: string | undefined): boolean =>
  !!message &&
  message.trim().length > 0 &&
  !isRawHttpStatus(message) &&
  !isFrameworkException(message) &&
  !isInternalDetail(message)

/**
 * @param message  the server's own message, if it sent one
 * @param status   the HTTP status, if there was a response
 * @param fallback the call site's contextual copy — used whenever the server's
 *                 message is missing or unfit. Preferred over one generic
 *                 string precisely BECAUSE it is contextual: "Could not start
 *                 checkout. Please try again." tells the reader more than
 *                 "Something went wrong" and carries no framework noise either.
 */
export const serverErrorMessage = ({
  message,
  status,
  fallback,
}: {
  message?: string
  status?: number
  fallback: string
}): string => {
  // Checked BEFORE the message, not after. `isFrameworkException` would already
  // reject the throttler's string and fall back — but the fallbacks say "please
  // try again", and trying again immediately is precisely what fails while a
  // rate limit is open. The status is the only thing that knows to say "wait".
  if (status === 429) return TOO_MANY_ATTEMPTS_MESSAGE

  return isReadableServerMessage(message) ? (message as string) : fallback
}
